import { type TFunction } from 'i18next'
import * as R from 'remeda'

import {
  type CellInfo,
  compact,
  type DataDisplayType,
  exists,
  getSortIconBySortValue,
  mapNineSegment,
  nineSegmentAttributeI18nKeyMap,
  type NineSegmentCounts,
  nineSegmentIndicatorLabelMap,
  nineSegmentNumberWithIntentionI18nKeyMap,
  NumFormat,
  reversedNineSegments,
  type RowInfo,
} from '~/utils'

import { calcAverage, calcMax } from './common'
import {
  type NineSegmentColumnKey,
  nineSegmentIndicatorHelpMap,
  nineSegmentIndicators,
} from './nineSegments'

// Minimal interface that both analysis page and basic output brandsData satisfy
type BrandData = {
  brandId?: number | string
  brandName: string
  brandNumber: number
  counts: NineSegmentCounts
  ratios: NineSegmentCounts & Record<string, number>
}

type HiddenRowId = number | string

type BuildNineSegmentsTableInfoParams = {
  addHighlightProps: (key: string) => Record<string, unknown>
  brandsData: BrandData[]
  dataDisplayType: DataDisplayType
  hiddenColumnKeys: NineSegmentColumnKey[]
  hiddenRowIds: HiddenRowId[]
  indicatorPadClassName: string
  sortValue: { ascending: boolean; columnKey: string } | null
  t: TFunction
  tamRatio: number
}

export const buildNineSegmentsTableInfo = ({
  addHighlightProps,
  brandsData,
  dataDisplayType,
  hiddenColumnKeys,
  hiddenRowIds,
  indicatorPadClassName,
  sortValue,
  t,
  tamRatio,
}: BuildNineSegmentsTableInfoParams): RowInfo<NineSegmentColumnKey>[] => {
  const visibleNineSegments = R.difference(
    reversedNineSegments,
    hiddenColumnKeys,
  )
  const firstNineSegment = visibleNineSegments[0]

  const visibleIndicators = R.difference(
    nineSegmentIndicators,
    hiddenColumnKeys,
  )
  const firstIndicator = visibleIndicators[0]

  const has8 = visibleNineSegments.includes('nineSegment8')
  const has7 = visibleNineSegments.includes('nineSegment7')
  const has6 = visibleNineSegments.includes('nineSegment6')
  const has5 = visibleNineSegments.includes('nineSegment5')
  const has4 = visibleNineSegments.includes('nineSegment4')
  const has3 = visibleNineSegments.includes('nineSegment3')
  const has2 = visibleNineSegments.includes('nineSegment2')
  const has1 = visibleNineSegments.includes('nineSegment1')
  const header2NineSegments = compact([
    has8 ? 'nineSegment8' : has7 ? 'nineSegment7' : false,
    has6 ? 'nineSegment6' : has5 ? 'nineSegment5' : false,
    has4 ? 'nineSegment4' : has3 ? 'nineSegment3' : false,
    has2 ? 'nineSegment2' : has1 ? 'nineSegment1' : false,
  ])
  const header2NineSegmentColSpans = compact([
    has8 ? (has7 ? 2 : 1) : has7 ? 1 : false,
    has6 ? (has5 ? 2 : 1) : has5 ? 1 : false,
    has4 ? (has3 ? 2 : 1) : has3 ? 1 : false,
    has2 ? (has1 ? 2 : 1) : has1 ? 1 : false,
  ])

  const visibleBrandsData = brandsData.filter(
    (brand) => !exists(brand.brandId) || !hiddenRowIds.includes(brand.brandId),
  )
  const average = calcAverage(visibleBrandsData)
  const max = calcMax(visibleBrandsData)

  return compact([
    // Header 1
    {
      brandNumber: { colSpan: 2, isColumnHeader: true },
      ...(exists(firstNineSegment)
        ? {
            [firstNineSegment]: {
              align: 'center',
              colSpan: visibleNineSegments.length,
              isColumnHeader: true,
              isHeader: true,
              text: t('9segs_distribution'),
            },
          }
        : {}),
      indicatorPad: {
        className: indicatorPadClassName,
        rowSpan: 5 + visibleBrandsData.length,
      },
      ...(exists(firstIndicator)
        ? {
            [firstIndicator]: {
              align: 'center',
              colSpan: visibleIndicators.length,
              isColumnHeader: true,
              isHeader: true,
              text: t('core_metrics'),
            },
          }
        : {}),
    },
    // Header 2
    {
      brandNumber: {
        align: 'center',
        colSpan: 2,
        isColumnHeader: true,
        isHeader: true,
        rowSpan: 2,
        text: t('brand_name_4'),
      },
      nineSegment9: {
        align: 'center',
        isColumnHeader: true,
        isHeader: true,
        text: t(nineSegmentAttributeI18nKeyMap.nineSegment9),
      },
      ...R.mapToObj(header2NineSegments, (nineSegment, index) => [
        nineSegment,
        {
          align: 'center',
          isColumnHeader: true,
          isHeader: true,
          colSpan: header2NineSegmentColSpans[index],
          text: t(nineSegmentAttributeI18nKeyMap[nineSegment]),
        } satisfies CellInfo,
      ]),

      ...R.mapToObj(nineSegmentIndicators, (indicator) => [
        indicator,
        {
          align: 'center',
          help: nineSegmentIndicatorHelpMap[indicator],
          icon: getSortIconBySortValue(sortValue, indicator),
          isColumnHeader: true,
          isHeader: true,
          rowSpan: 2,
          text: nineSegmentIndicatorLabelMap[indicator],
        } satisfies CellInfo,
      ]),
    },
    // Header 3
    {
      ...mapNineSegment((nineSegment): CellInfo => ({
        align: 'center',
        icon: getSortIconBySortValue(sortValue, nineSegment),
        isColumnHeader: true,
        isHeader: true,
        text: t(nineSegmentNumberWithIntentionI18nKeyMap[nineSegment]),
      })),
    },
    // Brand rows
    ...R.pipe(
      visibleBrandsData,
      R.map((brand): RowInfo<NineSegmentColumnKey> => ({
        brandNumber: { isHeader: true, text: brand.brandNumber },
        brandName: { isHeader: true, text: brand.brandName },
        ...mapNineSegment((nineSegment) => {
          const ratio = brand.ratios[nineSegment]
          const count = brand.counts[nineSegment]

          const text = NumFormat.forCount(
            count,
            ratio,
            tamRatio,
            dataDisplayType,
          )

          return {
            ratio,
            text,
            ...addHighlightProps(`${brand.brandName}-${nineSegment}`),
          }
        }),
        ...R.mapToObj(nineSegmentIndicators, (indicator) => {
          const ratio = brand.ratios[indicator]
          const text = NumFormat.forRatio(ratio)

          return [
            indicator,
            {
              ratio,
              text,
              ...addHighlightProps(`${brand.brandName}-${indicator}`),
            },
          ]
        }),
      })),
    ),
    // Average
    !hiddenRowIds.includes('average') && {
      brandNumber: { isHeader: true, colSpan: 2, text: t('average') },
      ...mapNineSegment((nineSegment): CellInfo => {
        if (!exists(average)) {
          return { text: '-' }
        }

        const ratio = average.ratio[nineSegment]
        const count = average.counts[nineSegment]

        return {
          text: NumFormat.forCount(count, ratio, tamRatio, dataDisplayType),
          ...addHighlightProps(`average-${nineSegment}`),
        }
      }),
      ...R.mapToObj(nineSegmentIndicators, (indicator) => [
        indicator,
        {
          text: exists(average)
            ? NumFormat.forRatio(average.ratio[indicator])
            : '-',
          ...addHighlightProps(`average-${indicator}`),
        },
      ]),
    },
    // Max
    !hiddenRowIds.includes('max') && {
      brandNumber: { isHeader: true, colSpan: 2, text: t('maximum_value') },
      ...mapNineSegment((nineSegment): CellInfo => {
        if (!exists(max)) {
          return { text: '-' }
        }

        const ratio = max.ratio[nineSegment]
        const count = max.counts[nineSegment]

        return {
          text: NumFormat.forCount(count, ratio, tamRatio, dataDisplayType),
          ...addHighlightProps(`max-${nineSegment}`),
        }
      }),
      ...R.mapToObj(nineSegmentIndicators, (indicator) => [
        indicator,
        {
          text: exists(max) ? NumFormat.forRatio(max.ratio[indicator]) : '-',
          ...addHighlightProps(`max-${indicator}`),
        },
      ]),
    },
  ])
}
